import {
    CloseButton,
    NumberInput as MantineNumberInput,
    type NumberInputProps as MantineNumberInputProps,
} from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';
import type { Ref } from 'react';

export type NumberInputValue = Exclude<MantineNumberInputProps['value'], undefined>;
export type NullableNumberInputValue = NumberInputValue | null;

export interface NumberInputProps extends Omit<MantineNumberInputProps, 'value' | 'defaultValue' | 'onChange'> {
    ref?: Ref<HTMLInputElement>;
    value?: NullableNumberInputValue;
    defaultValue?: NullableNumberInputValue;
    onChange?: (value: NullableNumberInputValue) => void;
    onClear?: () => void;
    nullable?: boolean;
}

const normalizeValue = (value: NullableNumberInputValue | undefined): NumberInputValue | undefined => {
    return value === null ? '' : value;
};

export const NumberInput = ({
    ref,
    size = 'xs',
    value,
    defaultValue,
    onChange,
    onClear,
    nullable = false,
    disabled,
    readOnly,
    rightSection,
    rightSectionPointerEvents,
    ...props
}: NumberInputProps) => {
    const [_value, setValue] = useUncontrolled<NumberInputValue>({
        value: normalizeValue(value),
        defaultValue: normalizeValue(defaultValue),
        finalValue: '',
        onChange: (nextValue) => {
            onChange?.(nullable && nextValue === '' ? null : nextValue);
        },
    });

    const showClearButton = nullable && _value !== '' && !disabled && !readOnly && !rightSection;

    const clearButton = showClearButton ? (
        <CloseButton
            aria-label="Clear value"
            size={size}
            mr={size}
            variant="transparent"
            tabIndex={-1}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
                setValue('');
                onClear?.();
            }}
        />
    ) : undefined;

    return (
        <MantineNumberInput
            ref={ref}
            size={size}
            value={_value}
            onChange={setValue}
            disabled={disabled}
            readOnly={readOnly}
            rightSection={rightSection ?? clearButton}
            rightSectionPointerEvents={rightSectionPointerEvents ?? (showClearButton ? 'all' : undefined)}
            {...props}
        />
    );
};
